import React from 'react';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import {
  TaskBaseline,
  FlattenedTask,
  TimelineConfig,
  BASELINE_HEIGHT,
  BAR_HEIGHT,
  BAR_PADDING,
} from '../types/gantt.types';
import { dateToPixel } from '../utils/dateUtils';

interface BaselineBarProps {
  baseline: TaskBaseline;
  task: FlattenedTask;
  config: TimelineConfig;
  color: string;
  index: number;
}

const BaselineBar: React.FC<BaselineBarProps> = ({
  baseline,
  task,
  config,
  color,
  index,
}) => {
  // Use passed color or fallback to gold
  const barColor = color || '#faad14';

  const startX = dateToPixel(baseline.startDate, config.startDate, config.dayWidth);
  const endX = dateToPixel(baseline.endDate, config.startDate, config.dayWidth);
  const width = Math.max(endX - startX, config.dayWidth / 2);

  // Position baseline bars below the main task bar
  const y =
    task.rowIndex * config.rowHeight +
    BAR_PADDING +
    BAR_HEIGHT +
    2 +
    index * (BASELINE_HEIGHT + 2);

  const tooltipContent = (
    <div style={{ fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Baseline: {baseline.baselineName}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Start:</span>
        <span>{dayjs(baseline.startDate).format('DD-MMM-YYYY')}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>End:</span>
        <span>{dayjs(baseline.endDate).format('DD-MMM-YYYY')}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(255,255,255,0.65)' }}>Duration:</span>
        <span>{baseline.duration} days</span>
      </div>

      {/* Variance from current */}
      <div
        style={{
          marginTop: 4,
          paddingTop: 4,
          borderTop: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span style={{ color: 'rgba(255,255,255,0.65)' }}>Start Variance:</span>
          <span
            style={{
              color:
                dayjs(task.startDate).diff(dayjs(baseline.startDate), 'day') > 0
                  ? '#ff7875'
                  : '#95de64',
            }}
          >
            {dayjs(task.startDate).diff(dayjs(baseline.startDate), 'day')} days
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span style={{ color: 'rgba(255,255,255,0.65)' }}>End Variance:</span>
          <span
            style={{
              color:
                dayjs(task.endDate).diff(dayjs(baseline.endDate), 'day') > 0
                  ? '#ff7875'
                  : '#95de64',
            }}
          >
            {dayjs(task.endDate).diff(dayjs(baseline.endDate), 'day')} days
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" color="rgba(0,0,0,0.85)">
      <rect
        x={startX}
        y={y}
        width={width}
        height={BASELINE_HEIGHT}
        rx={2}
        ry={2}
        fill={barColor}
        opacity={0.85}
        stroke={barColor}
        strokeWidth={1.5}
        style={{ cursor: 'pointer' }}
      />
    </Tooltip>
  );
};

export default BaselineBar;
